import {
  DeleteOutline,
  DownloadOutlined,
  DescriptionOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import { Button, CardContainer, Tag } from 'finara-components';
import React, { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';

import { ErrorState } from '@/components/common/ErrorState';
import { LoadingState } from '@/components/common/LoadingState';
import {
  useDeleteOrganizationDocument,
  useOrganizationDocuments,
  useUploadOrganizationDocuments,
} from '@/hooks/queries/useSuperAdminClients';

interface DocumentosTabProps {
  clientId: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const DocumentosTab: React.FC<DocumentosTabProps> = ({ clientId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documentType, setDocumentType] = useState('');

  const { data: documents = [], isLoading, isError, refetch } = useOrganizationDocuments(clientId);
  const uploadMutation = useUploadOrganizationDocuments(clientId);
  const deleteMutation = useDeleteOrganizationDocument(clientId);

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    try {
      await uploadMutation.mutateAsync({
        files,
        documentType: documentType.trim() || 'Documento',
      });
      toast.success('Documento(s) subido(s) exitosamente');
      setDocumentType('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al subir el documento';
      toast.error(message);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (documentId: string) => {
    try {
      await deleteMutation.mutateAsync(documentId);
      toast.success('Documento eliminado');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al eliminar el documento';
      toast.error(message);
    }
  };

  if (isLoading) {
    return <LoadingState message="Cargando documentos..." />;
  }

  if (isError) {
    return (
      <ErrorState
        title="Error al cargar los documentos"
        message="No se pudieron cargar los documentos. Por favor, intenta nuevamente."
        onRetry={() => void refetch()}
        showRetry
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Card */}
      <CardContainer>
        <h3 className="text-sm font-semibold text-brand-white mb-3">Subir Documento</h3>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label htmlFor="documentType" className="block text-sm font-medium mb-1">
              Tipo de documento
            </label>
            <input
              type="text"
              id="documentType"
              value={documentType}
              onChange={e => setDocumentType(e.target.value)}
              className="w-full px-3 py-2 border border-border bg-surface text-brand-white placeholder:text-muted rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue"
              placeholder="Ej: Acta constitutiva, Comprobante de domicilio"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="application/pdf,image/jpeg,image/png,image/jpg"
            className="hidden"
            onChange={e => void handleFilesSelected(e)}
          />
          <Button
            variant="black"
            startIcon={<UploadFileOutlined fontSize="small" />}
            disabled={uploadMutation.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadMutation.isPending ? 'Subiendo...' : 'Seleccionar archivo(s)'}
          </Button>
        </div>
        <p className="text-xs text-muted mt-2">Formatos permitidos: PDF, JPG, PNG (máx. 10MB).</p>
      </CardContainer>

      {/* Documents List */}
      <CardContainer>
        <h3 className="text-sm font-semibold text-brand-white mb-3">
          Documentos ({documents.length})
        </h3>

        {documents.length === 0 ? (
          <div className="text-center py-10 text-muted">
            <DescriptionOutlined fontSize="large" className="opacity-50 mb-2" />
            <p className="text-sm">Esta organización aún no tiene documentos.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between gap-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <DescriptionOutlined className="text-muted shrink-0" fontSize="small" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-brand-white truncate">
                        {doc.originalFilename}
                      </span>
                      {doc.fromOnboarding && <Tag variant="white">Onboarding</Tag>}
                    </div>
                    <div className="text-xs text-muted">
                      {doc.documentType} · {formatFileSize(doc.fileSize)} ·{' '}
                      {formatDate(doc.createdAt)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {doc.downloadUrl && (
                    <a
                      href={doc.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-muted hover:text-brand-white"
                      aria-label="Descargar documento"
                    >
                      <DownloadOutlined fontSize="small" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleDelete(doc.id)}
                    disabled={deleteMutation.isPending}
                    className="p-2 text-muted hover:text-red-400 disabled:opacity-50"
                    aria-label="Eliminar documento"
                  >
                    <DeleteOutline fontSize="small" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContainer>
    </div>
  );
};
